'use client';

import clsx from 'clsx';
import { useEffect, useState } from 'react';
import SubMenuDetail from './sub-menu-detail';
import SubMenuList from './sub-menu-list';
import { preloadMenuImage } from './menu-image-cache';

interface SubMenu {
    title: string;
    href: string;
    index: number;
    isScrollTo?: boolean;
}

interface MenuDetail {
    title: string;
    subtitle: string;
    imageUrl: string;
    imageDesc: string;
}

interface TopMenuPopoverProps {
    title: string;
    subMenus: SubMenu[];
    menuDetails: MenuDetail[];
    isDetailLoading?: boolean;
    hide: () => void;
}

function TopMenuPopover({
    title,
    subMenus,
    menuDetails,
    isDetailLoading = false,
    hide,
}: Readonly<TopMenuPopoverProps>) {
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const selectedDetail =
        selectedIndex === -1 ? null : menuDetails[selectedIndex];
    const [currentDetail, setCurrentDetail] = useState<MenuDetail | null>(null);
    const [previousDetail, setPreviousDetail] = useState<MenuDetail | null>(
        null,
    );

    useEffect(() => {
        menuDetails.forEach((detail) => {
            preloadMenuImage(detail.imageUrl);
        });
    }, [menuDetails]);

    useEffect(() => {
        if (isDetailLoading) {
            return;
        }

        if (!selectedDetail) {
            if (currentDetail) {
                setPreviousDetail(currentDetail);
            }
            setCurrentDetail(null);

            const timer = window.setTimeout(() => {
                setPreviousDetail(null);
            }, 140);

            return () => window.clearTimeout(timer);
        }

        const isSameDetail =
            currentDetail?.imageUrl === selectedDetail.imageUrl &&
            currentDetail?.title === selectedDetail.title &&
            currentDetail?.subtitle === selectedDetail.subtitle;

        if (isSameDetail) {
            return;
        }

        setPreviousDetail(currentDetail);
        setCurrentDetail(selectedDetail);

        const timer = window.setTimeout(() => {
            setPreviousDetail(null);
        }, 140);

        return () => window.clearTimeout(timer);
    }, [currentDetail, isDetailLoading, selectedDetail]);

    return (
        <div
            className={clsx('flex w-full items-center justify-center bg-black')}
        >
            <div className="flex h-[406px] w-[701px] flex-row py-[40px]">
                <div className="flex min-w-[160px] max-w-[160px] flex-col">
                    <div className="j-t4 pb-[4px] text-[#878789]">{title}</div>
                    <SubMenuList
                        hidePopover={hide}
                        menus={subMenus}
                        selectedIndex={selectedIndex}
                        setSelectedIndex={setSelectedIndex}
                    />
                </div>
                <div className="j-l1 flex w-full flex-row">
                    <div className="ml-[60px] mr-[80px] h-full w-[1px] bg-[#39383B]">
                        &nbsp;
                    </div>
                    <div className="relative min-h-[330px] w-full max-w-[400px] flex-1">
                        {isDetailLoading && selectedDetail !== null ? (
                            <SubMenuDetail
                                title={selectedDetail.title}
                                subtitle={selectedDetail.subtitle}
                                imageUrl={selectedDetail.imageUrl}
                                imageDesc={selectedDetail.imageDesc}
                                isLoading={true}
                                className="absolute inset-0"
                            />
                        ) : (
                            <>
                                {previousDetail && (
                                    <SubMenuDetail
                                        key={`previous-${previousDetail.imageUrl}-${previousDetail.title}`}
                                        title={previousDetail.title}
                                        subtitle={previousDetail.subtitle}
                                        imageUrl={previousDetail.imageUrl}
                                        imageDesc={previousDetail.imageDesc}
                                        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-150"
                                    />
                                )}
                                <SubMenuDetail
                                    key={`current-${currentDetail?.imageUrl ?? 'empty'}-${currentDetail?.title ?? 'empty'}`}
                                    title={currentDetail?.title ?? ''}
                                    subtitle={currentDetail?.subtitle ?? ''}
                                    imageUrl={currentDetail?.imageUrl ?? null}
                                    imageDesc={currentDetail?.imageDesc ?? null}
                                    className={clsx(
                                        'absolute inset-0 transition-[opacity,transform] duration-150 ease-out',
                                        currentDetail
                                            ? 'translate-x-0 opacity-100'
                                            : 'pointer-events-none translate-x-[6px] opacity-0',
                                    )}
                                />
                            </>
                        )}
                        {selectedIndex === -1 && currentDetail === null && (
                            <div className="inline-flex max-w-[400px] flex-1" />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default TopMenuPopover;
